import type { SocialsHandle } from "@bluframe/mightychroma-mightytool-frontend-types"
import { type ChangeEventHandler, useEffect, useMemo, useState } from "react"

import type { OverrideUpdatePayload } from "../types"

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max)

const parseNumberOrDefault = (value: string, fallback: number) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

export interface DraftOverridesState {
  draftNotes: string
  draftIsIgnored: boolean
  draftManualPriority: string
  isOverrideSaveDisabled: boolean
  onDraftNotesChange: ChangeEventHandler<HTMLTextAreaElement>
  onDraftIsIgnoredChange: (value: string | number) => void
  onDraftManualPriorityChange: (value: string) => void
  onJustInteracted: () => void
  onSaveOverrides: () => void
}

/**
 * Manages the Prospect Queue override draft state (notes/ignored/manualPriority)
 * for the currently selected handle.
 *
 * - Keeps draft state synced to `selectedHandle` when selection changes.
 * - Normalizes manualPriority to a 0..1 float before emitting updates.
 * - Computes a save-disabled flag when drafts match the current handle overrides.
 *
 * @param {SocialsHandle | null} selectedHandle
 * @param {(payload: OverrideUpdatePayload) => void} onOverrideUpdate
 * @returns {DraftOverridesState}
 *
 */
// eslint-disable-next-line max-statements
const useDraftOverrides = (
  selectedHandle: SocialsHandle | null,
  onOverrideUpdate: (payload: OverrideUpdatePayload) => void
): DraftOverridesState => {
  const [draftNotes, setDraftNotes] = useState("")
  const [draftIsIgnored, setDraftIsIgnored] = useState(false)
  const [draftManualPriority, setDraftManualPriority] = useState("0")

  const normalizedDraftManualPriority = useMemo(() => {
    const parsed = parseNumberOrDefault(draftManualPriority, 0)
    return clamp(parsed, 0, 1)
  }, [draftManualPriority])

  const isOverrideSaveDisabled = useMemo(() => {
    if (!selectedHandle) return true

    const currentNotes = selectedHandle.notes ?? ""
    const currentIgnored = Boolean(selectedHandle.isIgnored)
    const currentManualPriority = selectedHandle.manualPriority ?? 0

    return (
      draftNotes === currentNotes &&
      draftIsIgnored === currentIgnored &&
      normalizedDraftManualPriority === currentManualPriority
    )
  }, [
    draftIsIgnored,
    draftNotes,
    normalizedDraftManualPriority,
    selectedHandle
  ])

  useEffect(() => {
    let cancelled = false

    queueMicrotask(() => {
      if (cancelled) return

      if (!selectedHandle) {
        setDraftNotes("")
        setDraftIsIgnored(false)
        setDraftManualPriority("0")
        return
      }

      setDraftNotes(selectedHandle.notes ?? "")
      setDraftIsIgnored(Boolean(selectedHandle.isIgnored))
      setDraftManualPriority(String(selectedHandle.manualPriority ?? 0))
    })

    return () => {
      cancelled = true
    }
  }, [selectedHandle])

  const onDraftNotesChange: ChangeEventHandler<HTMLTextAreaElement> = (event) =>
    setDraftNotes(event.target.value)

  const onDraftIsIgnoredChange = () =>
    setDraftIsIgnored((previous) => !previous)

  const onDraftManualPriorityChange = (value: string) =>
    setDraftManualPriority(value)

  const onJustInteracted = () => {
    if (!selectedHandle) return

    onOverrideUpdate({
      accountId: selectedHandle.accountId,
      handleId: selectedHandle.id,
      latestInteractedAt: new Date().toISOString()
    })
  }

  const onSaveOverrides = () => {
    if (!selectedHandle) return

    onOverrideUpdate({
      accountId: selectedHandle.accountId,
      handleId: selectedHandle.id,
      isIgnored: draftIsIgnored,
      manualPriority: normalizedDraftManualPriority,
      notes: draftNotes === "" ? null : draftNotes
    })
  }

  return {
    draftIsIgnored,
    draftManualPriority,
    draftNotes,
    isOverrideSaveDisabled,
    onDraftIsIgnoredChange,
    onDraftManualPriorityChange,
    onDraftNotesChange,
    onJustInteracted,
    onSaveOverrides
  }
}

export default useDraftOverrides
